<template>
    <div class="module">
        <header class="module__header--with-disclaimer">
            <h1>Map</h1>
            <p class="disclaimer">
                We're updating our Maps interface and would appreciate your opinion. Send feedback to
                <a href="mailto:prosupport@gotenna.com">prosupport@goTenna.com</a>
            </p>
        </header>

        <section class="module__body">
            <div
                ref="mapContainer"
                class="map-container"
            >
                <PrimeDialog
                    v-model:visible="clearCallsign.isOpen"
                    modal
                    header="Clear Call Sign"
                    :style="{ width: '412px' }"
                    :pt="{ root: { 'data-test-id': 'clear-callsign-dialog' } }"
                >
                    <p>
                        Are you sure you want to clear
                        {{ clearCallsign.item?.name || clearCallsign.item?.getLabel() }} from the map? They will
                        reappear the next time they backhaul PLI to the portal.
                    </p>
                    <div class="clear-callsign-dismiss">
                        <input
                            id="dismissClearCallsignWarning"
                            v-model="clearCallsign.dismissWarning"
                            type="checkbox"
                            data-test-id="clear-callsign-dismiss-checkbox"
                        />
                        <label for="dismissClearCallsignWarning">Don’t show this message again</label>
                    </div>
                    <template #footer>
                        <button
                            class="btn"
                            type="button"
                            data-test-id="clear-callsign-cancel"
                            @click="clearCallsign.isOpen = false"
                        >
                            Cancel
                        </button>
                        <button
                            class="btn btn-primary"
                            type="button"
                            data-test-id="clear-callsign-continue"
                            @click="confirmClearCallsign()"
                        >
                            Continue
                        </button>
                    </template>
                </PrimeDialog>

                <PrimeToast
                    group="stale-user"
                    position="top-right"
                    :pt="{ root: { class: 'stale-alert-toast' } }"
                >
                    <template #container="{ message: toast, closeCallback }">
                        <div class="stale-alert-toast__body">
                            <div class="stale-alert-toast__content">
                                <h1>{{ toast.summary }}</h1>
                                <p data-test-id="stale-alert-detail">{{ toast.detail }}</p>
                            </div>
                            <div class="stale-alert-toast__actions">
                                <button
                                    type="button"
                                    data-test-id="stale-alert-action"
                                    @click="performStaleAlertAction(toast, closeCallback)"
                                >
                                    {{ toast.actionLabel }}
                                </button>
                                <button
                                    type="button"
                                    data-test-id="stale-alert-dismiss"
                                    @click="closeCallback"
                                >
                                    Dismiss
                                </button>
                            </div>
                        </div>
                    </template>
                </PrimeToast>
                <settings
                    v-if="showSettings"
                    :targetContainer="mapContainer"
                    @close="setSettingsDisplay(false)"
                    @cancel="setSettingsDisplay(false)"
                ></settings>

                <div class="map-panels">
                    <control-center
                        :userLocations="userLocations"
                        :pins="pins"
                        :shapes="shapes"
                        :showSettings="showSettings"
                        :confirmClearMapObjectFor="confirmClearMapObjectFor"
                        @toggleSettingsDisplay="setSettingsDisplay(!showSettings)"
                    />
                </div>
                <mission-map
                    ref="missionMap"
                    :userLocations="enabledUserLocations"
                    :groups="groups"
                    :pins="pins"
                    :shapes="shapes"
                    :mapContainer="mapContainer"
                    @clearMapClicked="clear"
                />
            </div>
        </section>
    </div>
</template>

<script>
import PrimeDialog from 'primevue/dialog'
import PrimeToast from 'primevue/toast'
import { mapGetters } from 'vuex'
import canConfirm from '@/mixins/canConfirm'
import Settings from '@/components/Map/Settings/index.vue'
import ControlCenter from '@/components/Map/ControlCenter/index.vue'
import MissionMap from '@/components/Map/MissionMap.vue'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import canToast from '@/mixins/canToast'

let refreshTimeout

export default {
    components: {
        PrimeDialog,
        PrimeToast,
        ControlCenter,
        MissionMap,
        Settings,
    },

    mixins: [canConfirm, needsOrganizationSlug, canToast],

    data() {
        return {
            clearCallsign: { key: null, item: null, dismissWarning: false, isOpen: false },
            mapContainer: null,
            refreshTimer: 15000,
            showSettings: false,
            showMessageComposer: false,
        }
    },

    computed: {
        ...mapGetters('mission', ['mission', 'mapSettings']),

        userLocations() {
            return this.mission.getUserLocations()
        },

        enabledUserLocations() {
            return this.userLocations.filter((userLocation) => userLocation.latitude)
        },

        groups() {
            return this.mission.getGroups()
        },

        pins() {
            return this.mission.getPins()
        },

        shapes() {
            return this.mission.getShapes()
        },
    },

    mounted() {
        this.refresh(true)
        this.mapContainer = this.$refs.mapContainer
        this.$store.dispatch('mission/getMapSettings')
    },

    beforeUnmount() {
        clearTimeout(refreshTimeout)
    },

    methods: {
        async refresh(shouldRecenter = false) {
            if (this.mission.get('id') === -1) {
                await this.$store.dispatch('mission/getDefaultMission')
            }
            clearTimeout(refreshTimeout)
            this.getMapData(shouldRecenter)
        },

        getMapData(shouldRecenter) {
            this.$store.dispatch('mission/getMapData').then(() => {
                if (shouldRecenter) {
                    refreshTimeout = setTimeout(() => {
                        if (this.enabledUserLocations.length) {
                            this.$refs.missionMap.recenter()
                        }
                    }, 0)
                }

                const { receiveStaleUserAlerts } = this.mapSettings

                const newStaleUsers = this.mission.getStaleUsers()
                if (receiveStaleUserAlerts && newStaleUsers.length) {
                    const content =
                        newStaleUsers.length === 1
                            ? `${newStaleUsers[0].getLabel()}'s`
                            : `${newStaleUsers[0].getLabel()} and ${newStaleUsers.length - 1} others'`

                    this.sendStaleAlert(content, newStaleUsers)
                }

                refreshTimeout = setTimeout(() => this.refresh(), this.refreshTimer)
            })
        },

        sendStaleAlert(content, users) {
            this.$toast.add({
                group: 'stale-user',
                severity: 'warn',
                summary: 'Stale User Alert',
                detail: `${content} updates not being received.`,
                life: 5000,
                actionLabel: users.length === 1 ? 'Locate' : 'See All',
                onAction: () => {
                    this.$root.$emit(users.length === 1 ? 'centerOn' : 'displayStaleUsers', users[0])
                },
            })
        },

        /**
         * The alert's action button; dismissing it is just closing the toast.
         *
         * @param {object} toast
         * @param {Function} closeCallback
         */
        performStaleAlertAction(toast, closeCallback) {
            closeCallback()
            toast.onAction?.()
        },

        async clear() {
            const confirmed = await this.confirm({
                message: 'Are you sure you want to permanently delete all map objects?',
                acceptLabel: 'Delete',
                danger: true,
            })

            if (!confirmed) {
                return
            }

            const mapObjectsToRemove = {
                user_locations: this.mission.getUserLocations().map((userLocation) => userLocation.gid),
                pins: this.mission.getPins().map((pin) => pin.id),
                shapes: this.mission.getShapes().map((shape) => shape.id),
                groups: this.mission.getGroups().map((group) => group.id),
            }

            this.removeMapObjects(mapObjectsToRemove).then(() => {
                this.mapGroups = []
                this.mission.clearAllMapObjects()
            })
        },

        confirmClearMapObjectFor(key) {
            return (item) => {
                const dismissedCallsignWarning =
                    this.$store.getters['mission/hasDismissedMessage']('dismissClearCallsignWarning')

                if (dismissedCallsignWarning) {
                    this.clearMapObject(key, item)
                    return
                }

                // The prompt carries its own "don't show again" checkbox, so
                // it is a small dialog of its own rather than a confirmation.
                this.clearCallsign = { key, item, dismissWarning: false, isOpen: true }
            }
        },

        confirmClearCallsign() {
            const { key, item, dismissWarning } = this.clearCallsign

            this.clearCallsign = { ...this.clearCallsign, isOpen: false }

            if (dismissWarning) {
                this.$store.dispatch('addDismissedNotification', 'dismissClearCallsignWarning')
            }

            this.clearMapObject(key, item)
        },

        clearMapObject(key, item) {
            const identifier = item.get('identifier')

            const data = {
                [key]: [item.get(identifier)],
            }

            this.removeMapObjects(data).then(() => {
                this.mission.clearMapObject(item)
            })
        },

        setSettingsDisplay(display) {
            this.showSettings = display
        },

        removeMapObjects(mapObjects) {
            return this.$http.post(`/${this.organizationSlug}/missions/${this.mission.get('id')}/map-data`, mapObjects)
        },
    },
}
</script>
